import { CommonModule, formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { KycAccordionComponent } from '@shared/ui/kyc-accordion/kyc-accordion.component';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { InputComponent } from '@shared/ui/input/input.component';
import {
  ButtonDirective,
  SpinDirective,
} from '@shared/directives/button.directive';
import { BaseSelectDirective } from '@shared/directives/base-select.directive';
import { InputDateDirective } from '@shared/directives/input-date.directive';
import { ICreateCustomer, JointPersonaldetails } from '@core/models/admin/account/joint';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  Observable,
  Subscription,
} from 'rxjs';
import { CustomValidators } from '@core/validators/custom-validator';
import { JointRegistrationService } from '@core/services/joint-registration.service';
import { LoadingService } from '@core/services/loading.service';
import { ActivatedRoute } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import { NIGERIAN_STATES } from '@core/data/nigerian-states';
import { COUNTRIES } from '@core/data/countries-states';
import {
  ANNUAL_INCOME,
  GENDER,
  MARITAL_STATUS,
  NIGERIAN_TITLES,
  FUND_SOURCE,
} from 'app/app.constants';
import { ReportService } from '@core/services/report.service';
import { CommonService } from '@core/services/common.service';

interface ProfileFormState {
  form: FormGroup;
  isUpdating: BehaviorSubject<boolean>;
  hasProfile: BehaviorSubject<boolean>;
}

@Component({
  selector: 'app-edit-joint-customer-information',
  standalone: true,
  imports: [
    CommonModule,
    KycAccordionComponent,
    ReactiveFormsModule,
    InputLabelComponent,
    InputComponent,
    ButtonDirective,
    BaseSelectDirective,
    InputDateDirective,
    SpinDirective,
  ],
  templateUrl: './edit-joint-customer-information.component.html',
  styleUrl: './edit-joint-customer-information.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditJointCustomerInformationComponent
  implements OnInit, OnDestroy
{
  @Input() personalDetails!: JointPersonaldetails[];

  subs: Subscription[] = [];
  nigerianStates = NIGERIAN_STATES;
  countries = COUNTRIES.data;
  titles = Object.entries(NIGERIAN_TITLES).map(([key, value]) => ({
    key,
    value,
  }));
  annualIncomeRange: any[] = [];
  maritalStatus = MARITAL_STATUS;
  genderList = GENDER;
  sourceoffunds: any = FUND_SOURCE;
  isLoading$!: Observable<boolean>;
  accountId!: string;

  activeProfileIndex = 0;
  profileTabs: { label: string }[] = [];
  profileFormStates: ProfileFormState[] = [];

  private jointRegService = inject(JointRegistrationService);
  private loadingService = inject(LoadingService);
  private fb = inject(FormBuilder);
  private toast = inject(NotificationFacade);
  private route = inject(ActivatedRoute);
  private reportService = inject(ReportService);
  private change = inject(ChangeDetectorRef);
  private commonService = inject(CommonService);

  constructor() {}

  ngOnInit(): void {
    this.getAllEnums();
    this.accountId = this.route.snapshot.parent?.params['accountId'];

    if (this.personalDetails?.length) {
      // Initialize profile form states dynamically
      this.personalDetails.forEach((profile, index) => {
        const formState: ProfileFormState = {
          form: this.createProfileForm(),
          isUpdating: new BehaviorSubject<boolean>(false),
          hasProfile: new BehaviorSubject<boolean>(true),
        };
        this.profileFormStates.push(formState);
      });

      this.buildProfileTabs();
      this.setFormValues();
    }
  }

  onTabChange(index: number): void {
    this.activeProfileIndex = index;
    this.change.markForCheck();
  }

  private buildProfileTabs(): void {
    this.profileTabs = this.personalDetails.map((profile, index) => {
      const fullName = `${profile.first_name} ${profile.last_name}`.trim();
      return {
        label: fullName || `Profile ${index + 1}`,
      };
    });
  }

  getAllEnums() {
    const sub = this.reportService.getAllEnums().subscribe({
      next: (response) => {
        this.annualIncomeRange = this.convertApiToDropdown(
          response?.data?.AnnualIncome
        );
        this.change.detectChanges();
      },
      error: (resp) => {
        this.change.detectChanges();
      },
    });
    this.subs.push(sub);
  }

  convertApiToDropdown(apiData: string[]): { value: string; label: string }[] {
    return apiData.map((item) => {
      return { value: item, label: item };
    });
  }

  createProfileForm(): FormGroup {
    return this.fb.group({
      title: [''],
      first_name: ['', Validators.minLength(2)],
      last_name: ['', Validators.minLength(2)],
      middle_name: ['', Validators.minLength(2)],
      gender: [''],
      email: [''],
      phone_number_1: [''],
      phone_number_2: [''],
      dob: [new Date()],
      marital_status: [''],
      nationality: [''],
      annual_income: [''],
      source_of_fund: [''],
      state_of_origin: [''],
      political_exposure: [''],
      place_of_birth: [''],
      bvn: ['', [Validators.required, CustomValidators.bvn()]],
      profile_id: [''],
    });
  }

  setFormValues(): void {
    this.personalDetails.forEach((profile, index) => {
      if (index < this.profileFormStates.length) {
        const state = this.profileFormStates[index];
        state.form.patchValue({
          title: profile.title || '',
          first_name: profile.first_name,
          last_name: profile.last_name,
          middle_name: profile.middle_name,
          email: profile.email,
          gender: profile.gender || '',
          phone_number_1: profile.phone_number_1 || '',
          phone_number_2: profile.phone_number_2 || '',
          marital_status: profile.marital_status || '',
          nationality: profile.nationality || '',
          annual_income: profile.annual_income || '',
          source_of_fund: profile.source_of_fund || '',
          state_of_origin: profile.state_of_origin || '',
          bvn: profile.bvn || '',
          political_exposure: `${profile.political_exposure}`,
          place_of_birth: profile.place_of_birth || '',
          profile_id: profile.id,
        });

        // setting this separately because it is a date
        state.form.controls['dob'].setValue(
          formatDate(profile.dob ?? '', 'yyyy-MM-dd', 'en')
        );
      }
    });
  }

  updateProfile(profileIndex: number): void {
    if (profileIndex >= this.profileFormStates.length) return;

    const state = this.profileFormStates[profileIndex];
    state.isUpdating.next(true);

    const formValue = state.form.getRawValue();
    formValue.account_id = this.accountId;

    const sub = this.jointRegService.updateCustomerProfile(formValue).subscribe({
      next: () => {
        state.isUpdating.next(false);
        this.toast.success('Successfully updated Customer Information');
      },
      error: () => {
        state.isUpdating.next(false);
        this.toast.error('Failed to update Customer Information');
      },
    });
    this.subs.push(sub);
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
